import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Star, CheckCircle2, Loader2 } from 'lucide-react';
import { Button } from './ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATS = [
  { key: 'salon', label: 'Salon / Tesis', desc: 'Saha ve tesis memnuniyeti' },
  { key: 'hakem', label: 'Hakem', desc: 'Hakem performansı' },
  { key: 'organizasyon', label: 'Organizasyon', desc: 'Genel organizasyon' },
  { key: 'iletisim', label: 'İletişim', desc: 'Bizimle iletişim' },
];

const StarRow = ({ value, onChange, testid }) => (
  <div className="flex gap-1.5" data-testid={testid}>
    {[1, 2, 3, 4, 5].map((n) => (
      <button
        key={n}
        type="button"
        onClick={() => onChange(n)}
        className="transition-transform hover:scale-110 focus:outline-none"
        data-testid={`${testid}-star-${n}`}
        aria-label={`${n} yıldız`}
      >
        <Star className={`w-8 h-8 ${n <= value ? 'fill-amber-400 text-amber-400' : 'text-slate-300'}`} />
      </button>
    ))}
  </div>
);

const FeedbackPage = ({ reservationId }) => {
  const [ctx, setCtx] = useState(null);
  const [loading, setLoading] = useState(true);
  const [ratings, setRatings] = useState({ salon: 0, hakem: 0, organizasyon: 0, iletisim: 0 });
  const [comment, setComment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/feedback/reservation/${reservationId}`);
        setCtx(res.data);
        if (res.data.already_submitted) setDone(true);
      } catch (e) {
        setError('Rezervasyon bulunamadı veya link geçersiz.');
      } finally {
        setLoading(false);
      }
    })();
  }, [reservationId]);

  const submit = async () => {
    if (Object.values(ratings).some((v) => v < 1)) {
      setError('Lütfen tüm kategorileri puanlayın.');
      return;
    }
    setError('');
    setSubmitting(true);
    try {
      await axios.post(`${API}/feedback`, { reservation_id: reservationId, ...ratings, comment });
      setDone(true);
    } catch (e) {
      if (e?.response?.status === 409) setDone(true);
      else setError('Gönderilemedi, lütfen tekrar deneyin.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center bg-slate-50"><Loader2 className="w-8 h-8 animate-spin text-emerald-600" /></div>;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-teal-50 flex items-center justify-center p-4">
      <div className="w-full max-w-lg bg-white rounded-2xl shadow-xl border border-slate-100 overflow-hidden" data-testid="feedback-page">
        <div className="bg-gradient-to-r from-emerald-600 to-teal-600 text-white p-6">
          <h1 className="text-2xl font-bold">Deneyiminizi Değerlendirin</h1>
          <p className="text-emerald-50 text-sm mt-1">Avrasya Sportif — görüşleriniz bizim için değerli</p>
        </div>

        {done ? (
          <div className="p-10 text-center" data-testid="feedback-thankyou">
            <CheckCircle2 className="w-16 h-16 text-emerald-500 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-slate-800">Teşekkür ederiz!</h2>
            <p className="text-slate-500 mt-2">Geri bildiriminiz başarıyla alındı.</p>
          </div>
        ) : error && !ctx ? (
          <div className="p-10 text-center text-slate-600">{error}</div>
        ) : (
          <div className="p-6 space-y-5">
            {ctx && (
              <p className="text-sm text-slate-500">
                Merhaba <span className="font-semibold text-slate-800">{ctx.customer_name}</span>,
                {ctx.salon_name ? ` ${ctx.salon_name}` : ''} {ctx.date ? `(${ctx.date})` : ''} organizasyonunuzu puanlayın.
              </p>
            )}
            {CATS.map((c) => (
              <div key={c.key} className="flex items-center justify-between gap-3 border-b border-slate-100 pb-4">
                <div>
                  <p className="font-semibold text-slate-800">{c.label}</p>
                  <p className="text-xs text-slate-400">{c.desc}</p>
                </div>
                <StarRow value={ratings[c.key]} onChange={(v) => setRatings({ ...ratings, [c.key]: v })} testid={`rating-${c.key}`} />
              </div>
            ))}
            <div>
              <label className="text-sm font-medium text-slate-700">Yorumunuz (opsiyonel)</label>
              <textarea
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                rows={3}
                placeholder="Eklemek istedikleriniz..."
                className="mt-1 w-full rounded-lg border border-slate-200 p-3 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-400"
                data-testid="feedback-comment"
              />
            </div>
            {error && <p className="text-sm text-rose-600" data-testid="feedback-error">{error}</p>}
            <Button
              onClick={submit}
              disabled={submitting}
              className="w-full h-12 bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white font-bold"
              data-testid="feedback-submit"
            >
              {submitting ? 'Gönderiliyor...' : 'Gönder'}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default FeedbackPage;
